/**
 * Client tool-runner / HITL bridge.
 *
 * Routes a client-forwarded tool call: the READ tool (`list_structures`)
 * auto-runs with no human gate and returns metadata; every other tool is
 * routed to the gate. Fail-closed — an unknown/mutating tool is NEVER
 * auto-run (defence-in-depth; the server also enforces this gate). Pure and
 * React-free; the structure projection is injected so this is unit-testable
 * without Recoil/GPU.
 */
import type { ListedStructure, ListStructuresInput } from '../../contract';

// Tool names come from the contract's zero-dependency leaf (value-safe: the
// leaf imports no zod/AI-SDK runtime, so nothing crosses into the bundle).
// Imported for local use, then re-published as this module's surface.
import {
    LIST_STRUCTURES_TOOL_NAME,
    APPLY_SYMMETRIC_MARGIN_TOOL_NAME,
    CREATE_RING_TOOL_NAME,
    APPLY_ASYMMETRIC_MARGIN_TOOL_NAME,
    BOOLEAN_OPERATION_TOOL_NAME,
    ANALYZE_IMAGE_FOR_MODEL_SELECTION_TOOL_NAME,
    PROPOSE_CONTOUR_MODEL_TOOL_NAME,
    START_AUTO_SEGMENTATION_TOOL_NAME,
    SEARCH_PATIENTS_TOOL_NAME,
    LIST_PATIENT_STUDIES_TOOL_NAME,
    PROPOSE_LOAD_SERIES_TOOL_NAME,
    LIST_IMPORTED_PATIENTS_TOOL_NAME,
    CHECK_PACS_CONNECTION_TOOL_NAME,
    FILL_CAVITIES_TOOL_NAME,
    REMOVE_SMALL_PARTS_TOOL_NAME,
    CLEAR_CONTOURS_TOOL_NAME,
    ZOOM_VIEW_TOOL_NAME,
    CHECK_AUTO_SEGMENTATION_JOBS_TOOL_NAME,
    CANCEL_AUTO_SEGMENTATION_TOOL_NAME,
    SEND_STRUCTURE_SET_TO_PACS_TOOL_NAME,
    SET_STRUCTURE_VISIBILITY_TOOL_NAME,
    GO_TO_POSITION_TOOL_NAME,
    SET_WINDOW_LEVEL_TOOL_NAME,
    SET_STRUCTURE_COLOR_TOOL_NAME,
    INTERPOLATE_STRUCTURE_TOOL_NAME,
    SMOOTH_STRUCTURE_TOOL_NAME,
    MOVE_STRUCTURE_TOOL_NAME,
    RENAME_STRUCTURE_TOOL_NAME,
    DELETE_STRUCTURE_TOOL_NAME,
    DUPLICATE_STRUCTURE_TOOL_NAME,
    REPLACE_STRUCTURE_CONTOURS_TOOL_NAME,
    SET_STRUCTURE_TYPE_TOOL_NAME,
    CREATE_MARKER_TOOL_NAME,
    SET_MARKER_POSITION_TOOL_NAME,
    CREATE_STRUCTURE_SET_TOOL_NAME,
    EDIT_STRUCTURE_SET_INFO_TOOL_NAME,
    SET_STRUCTURE_SET_APPROVAL_TOOL_NAME,
    GET_STRUCTURE_PROPERTIES_TOOL_NAME,
    RESET_DISPLAY_TOOL_NAME,
    SHOW_IN_VIEWER_TOOL_NAME,
    LIST_TIMEPOINTS_TOOL_NAME,
    BRING_PRIOR_CONTOUR_TOOL_NAME,
    COMPARE_STRUCTURES_TOOL_NAME,
    MUTATING_TOOL_NAMES,
} from '@agent/tool-names';
import { asRecord } from './mutatingToolRunner';

export {
    LIST_STRUCTURES_TOOL_NAME,
    APPLY_SYMMETRIC_MARGIN_TOOL_NAME,
    CREATE_RING_TOOL_NAME,
    APPLY_ASYMMETRIC_MARGIN_TOOL_NAME,
    BOOLEAN_OPERATION_TOOL_NAME,
    ANALYZE_IMAGE_FOR_MODEL_SELECTION_TOOL_NAME,
    PROPOSE_CONTOUR_MODEL_TOOL_NAME,
    START_AUTO_SEGMENTATION_TOOL_NAME,
    SEARCH_PATIENTS_TOOL_NAME,
    LIST_PATIENT_STUDIES_TOOL_NAME,
    PROPOSE_LOAD_SERIES_TOOL_NAME,
    LIST_IMPORTED_PATIENTS_TOOL_NAME,
    CHECK_PACS_CONNECTION_TOOL_NAME,
    FILL_CAVITIES_TOOL_NAME,
    REMOVE_SMALL_PARTS_TOOL_NAME,
    CLEAR_CONTOURS_TOOL_NAME,
    ZOOM_VIEW_TOOL_NAME,
    CHECK_AUTO_SEGMENTATION_JOBS_TOOL_NAME,
    CANCEL_AUTO_SEGMENTATION_TOOL_NAME,
    SEND_STRUCTURE_SET_TO_PACS_TOOL_NAME,
    SET_STRUCTURE_VISIBILITY_TOOL_NAME,
    GO_TO_POSITION_TOOL_NAME,
    SET_WINDOW_LEVEL_TOOL_NAME,
    SET_STRUCTURE_COLOR_TOOL_NAME,
    INTERPOLATE_STRUCTURE_TOOL_NAME,
    SMOOTH_STRUCTURE_TOOL_NAME,
    MOVE_STRUCTURE_TOOL_NAME,
    RENAME_STRUCTURE_TOOL_NAME,
    DELETE_STRUCTURE_TOOL_NAME,
    DUPLICATE_STRUCTURE_TOOL_NAME,
    REPLACE_STRUCTURE_CONTOURS_TOOL_NAME,
    SET_STRUCTURE_TYPE_TOOL_NAME,
    CREATE_MARKER_TOOL_NAME,
    SET_MARKER_POSITION_TOOL_NAME,
    CREATE_STRUCTURE_SET_TOOL_NAME,
    EDIT_STRUCTURE_SET_INFO_TOOL_NAME,
    SET_STRUCTURE_SET_APPROVAL_TOOL_NAME,
    GET_STRUCTURE_PROPERTIES_TOOL_NAME,
    RESET_DISPLAY_TOOL_NAME,
    SHOW_IN_VIEWER_TOOL_NAME,
    LIST_TIMEPOINTS_TOOL_NAME,
    BRING_PRIOR_CONTOUR_TOOL_NAME,
    COMPARE_STRUCTURES_TOOL_NAME,
    MUTATING_TOOL_NAMES,
};

export interface ToolCallLike {
    readonly toolName: string;
    readonly toolCallId: string;
    readonly input: unknown;
}

/** Lazily provides the current structure list (injected; may be async). */
export interface ToolRunnerDeps {
    readonly structures: () => Promise<ReadonlyArray<ListedStructure>>;
}

export type ToolRunOutcome =
    | {
          readonly kind: 'auto-output';
          readonly toolName: string;
          readonly toolCallId: string;
          readonly output: ReadonlyArray<ListedStructure>;
      }
    | {
          readonly kind: 'requires-gate';
          readonly toolName: string;
          readonly toolCallId: string;
      };

/**
 * Read the optional `nameContains` filter from untyped tool input. Typed
 * against the shared contract input so a server rename of the field is a
 * compile error here, not a silently no-op filter that returns every structure.
 */
function readNameContains(input: unknown): string | undefined {
    const record = asRecord(input) as Partial<ListStructuresInput> | undefined;
    const value = record?.nameContains;
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export async function runToolCall(
    call: ToolCallLike,
    deps: ToolRunnerDeps
): Promise<ToolRunOutcome> {
    if (call.toolName === LIST_STRUCTURES_TOOL_NAME) {
        const structures = await deps.structures();
        const nameContains = readNameContains(call.input);
        const output =
            nameContains === undefined
                ? structures
                : structures.filter((s) =>
                      s.name.toLowerCase().includes(nameContains.toLowerCase())
                  );
        return {
            kind: 'auto-output',
            toolName: call.toolName,
            toolCallId: call.toolCallId,
            output,
        };
    }

    // Fail-closed: anything that is not the known read tool requires the
    // human gate and is never auto-run.
    return {
        kind: 'requires-gate',
        toolName: call.toolName,
        toolCallId: call.toolCallId,
    };
}
